import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import { bottomTabsFor, isPublicPath } from '../mobile/navModel';
import { openMoreSheet } from '../mobile/chrome/MobileShell';
import './BottomNav.css';

/**
 * App-style bottom tab bar — phones only (CSS-gated at ≤ 768px).
 *
 * Up to four destinations for the signed-in role (src/mobile/navModel `bottomTabsFor`) plus
 * «المزيد», which opens the More sheet with everything else. «طلب جديد» is the raised gold
 * button in the middle. Hidden on customer link pages and when signed out.
 */
const BottomNav: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  if (!isAuthenticated || !user) return null;
  if (isPublicPath(pathname)) return null;

  const tabs = bottomTabsFor(user);
  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));
  // `/orders/create` must not light both its own tab and «الطلبات».
  const active = tabs.filter((tab) => isActive(tab.to)).sort((a, b) => b.to.length - a.to.length)[0];

  return (
    <nav className="bottom-nav" aria-label={t('bottomNav.aria')}>
      {tabs.map((tab) => {
        const on = active?.to === tab.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`bottom-nav-item${on ? ' active' : ''}${tab.primary ? ' is-primary' : ''}`}
            aria-current={on ? 'page' : undefined}
          >
            <span className="bottom-nav-icon"><Icon name={tab.icon} size={tab.primary ? 26 : 23} /></span>
            <span className="bottom-nav-label">{t(tab.labelKey)}</span>
          </Link>
        );
      })}
      <button type="button" className="bottom-nav-item bottom-nav-more" onClick={openMoreSheet}>
        <span className="bottom-nav-icon"><Icon name="more" size={23} /></span>
        <span className="bottom-nav-label">{t('bottomNav.more')}</span>
      </button>
    </nav>
  );
};

export default BottomNav;
